import {Howler, HowlOptions} from 'howler'
import {debugLogger} from '../utils/debugLogger'

interface IAudioObject {
  key: string
  options: HowlOptions
}

const LOCALSTORAGE_KEY = 'audio_muted'

class AudioManager {
  private static _instance: AudioManager
  private static _isMuted: boolean = false

  BGM: {[key: string]: Howl} = {}
  SFX: {[key: string]: Howl} = {}

  constructor() {
    if (AudioManager._instance) {
      return AudioManager._instance
    }
    AudioManager._instance = this

    this.init()
  }

  /**
   * Initialize the audio manager
   */
  private init() {
    // Load mute state from local storage and set Howler mute state
    const isMuted = localStorage.getItem(LOCALSTORAGE_KEY) === 'true'
    AudioManager._isMuted = isMuted
    Howler.mute(isMuted)
  }

  /**
   * Register a bgm audio object to the library
   * @param audioObject
   */
  registerBgm(audioObject: IAudioObject) {
    const howlObject = new Howl(audioObject.options)

    howlObject.on('play', () => {
      debugLogger({
        message: `Play: ${audioObject.key}`,
        tag: 'BGM',
      })
    })

    howlObject.on('stop', () => {
      debugLogger({
        message: `Stop: ${audioObject.key}`,
        tag: 'BGM',
      })
    })

    this.BGM[audioObject.key] = howlObject

    if (this.BGM[audioObject.key]) {
      debugLogger({
        message: `Registered BGM: ${audioObject.key}`,
        tag: 'AudioManager',
      })
    }
  }

  /**
   * Register a sfx audio object to the library
   * @param audioObject
   */
  registerSfx(audioObject: IAudioObject) {
    const howlObject = new Howl(audioObject.options)

    howlObject.on('play', () => {
      debugLogger({
        message: `Play: ${audioObject.key}`,
        tag: 'SFX',
      })
    })

    this.SFX[audioObject.key] = howlObject

    if (this.SFX[audioObject.key]) {
      debugLogger({
        message: `Registered SFX: ${audioObject.key}`,
        tag: 'AudioManager',
      })
    }
  }

  /**
   * Switch to a new BGM and stop any currently playing BGM
   * @param key
   */
  switchBgm(key: string, fadeToVolume: number = 1) {
    const toAudio = this.BGM[key]

    if (toAudio?.playing()) {
      return
    }

    this.resetAllBgm(toAudio)

    if (toAudio) {
      toAudio.seek(0)
      toAudio.fade(0, fadeToVolume, 2000).play()
    }
  }

  /**
   * Stop all BGM and rewind to start
   */
  resetAllBgm(toAudio?: Howl | undefined) {
    Object.values(this.BGM).forEach(audio => {
      // @ts-ignore
      // logged object has _src property but ts reports it does not
      if (toAudio?._src !== audio._src) {
        audio.fade(audio.volume(), 0, 1000).stop().seek(0)
      }
    })
  }

  /**
   * Stop all sfx and rewind to start
   */
  resetAllSfx() {
    Object.values(this.SFX).forEach(audio => audio.stop().seek(0))
  }

  /**
   * Reset all audio
   */
  reset() {
    this.resetAllBgm()
    this.resetAllSfx()
  }

  /**
   * Toggle mute state and save to local storage
   */
  toggleMute() {
    AudioManager._isMuted = !AudioManager._isMuted

    Howler.mute(AudioManager._isMuted)
    localStorage.setItem(LOCALSTORAGE_KEY, AudioManager._isMuted.toString())
  }

  getIsMuted() {
    return AudioManager._isMuted
  }
}

export default AudioManager
